import { Data, Effect } from 'effect';
import { z, type ZodType } from 'zod';
import type { JsonSchema, StructuredSchema } from './types';

export interface NormalizedStructuredSchema<T> {
  jsonSchema: JsonSchema;
  parser?: ZodType<T>;
}

export interface StructuredValidationSuccess<T> {
  success: true;
  data: T;
}

export interface StructuredValidationFailure {
  success: false;
  issues: string[];
}

interface InternalStructuredValidationFailure extends StructuredValidationFailure {
  safeIssues: string[];
}

type InternalStructuredValidationResult<T> =
  | StructuredValidationSuccess<T>
  | InternalStructuredValidationFailure;

export type StructuredSchemaFailureStage = 'normalize' | 'cache_key' | 'validate';

/**
 * Safe structured-output failure. Source schemas, decoded values, and raw causes are
 * intentionally excluded because callers commonly surface this error in UI logs.
 */
export class StructuredSchemaError extends Data.TaggedError('StructuredSchemaError')<{
  readonly stage: StructuredSchemaFailureStage;
  readonly issues: readonly string[];
  readonly message: string;
}> {}

function structuredSchemaFailure(
  stage: StructuredSchemaFailureStage,
  issue: string,
): StructuredSchemaError {
  return new StructuredSchemaError({ stage, issues: [issue], message: issue });
}

function structuredSchemaIssues(
  stage: StructuredSchemaFailureStage,
  issues: readonly string[],
): StructuredSchemaError {
  return new StructuredSchemaError({ stage, issues, message: issues.join('; ') });
}

export function normalizeStructuredSchemaEffect<T>(
  schema: StructuredSchema<T>,
): Effect.Effect<NormalizedStructuredSchema<T>, StructuredSchemaError> {
  return Effect.try({
    try: () => normalizeStructuredSchemaUnsafe(schema),
    catch: () =>
      structuredSchemaFailure(
        'normalize',
        'The structured schema could not be converted to JSON Schema.',
      ),
  });
}

export function normalizeStructuredSchema<T>(
  schema: StructuredSchema<T>,
): NormalizedStructuredSchema<T> {
  const outcome = Effect.runSync(
    Effect.match(normalizeStructuredSchemaEffect(schema), {
      onFailure: (error) => ({ success: false as const, error }),
      onSuccess: (normalized) => ({ success: true as const, normalized }),
    }),
  );
  if (!outcome.success) throw outcome.error;
  return outcome.normalized;
}

function normalizeStructuredSchemaUnsafe<T>(
  schema: StructuredSchema<T>,
): NormalizedStructuredSchema<T> {
  const maybeZod = schema as ZodType<T>;
  const hasSafeParse = typeof (maybeZod as { safeParse?: unknown }).safeParse === 'function';
  if (hasSafeParse) {
    return {
      jsonSchema: z.toJSONSchema(maybeZod, { target: 'draft-2020-12' }) as JsonSchema,
      parser: maybeZod,
    };
  }
  return { jsonSchema: schema as JsonSchema };
}

export function structuredSchemaCacheKeyEffect<T>(
  schema: StructuredSchema<T>,
): Effect.Effect<string, StructuredSchemaError> {
  return Effect.gen(function* () {
    const normalized = yield* normalizeStructuredSchemaEffect(schema);
    return yield* Effect.try({
      try: () => {
        const key = JSON.stringify(normalized.jsonSchema);
        if (typeof key !== 'string') throw new TypeError('JSON Schema was not serializable.');
        return key;
      },
      catch: () =>
        structuredSchemaFailure('cache_key', 'The structured schema could not be serialized.'),
    });
  });
}

export function structuredSchemaCacheKey<T>(schema: StructuredSchema<T>): string {
  const outcome = Effect.runSync(
    Effect.match(structuredSchemaCacheKeyEffect(schema), {
      onFailure: (error) => ({ success: false as const, error }),
      onSuccess: (key) => ({ success: true as const, key }),
    }),
  );
  if (!outcome.success) throw outcome.error;
  return outcome.key;
}

export function decodeStructuredDataEffect<T>(
  schema: StructuredSchema<T>,
  value: unknown,
): Effect.Effect<T, StructuredSchemaError> {
  return Effect.gen(function* () {
    const parsed = yield* validateStructuredDataInternalEffect(schema, value);
    if (!parsed.success) {
      return yield* Effect.fail(structuredSchemaIssues('validate', parsed.safeIssues));
    }
    return parsed.data;
  });
}

export function validateStructuredDataEffect<T>(
  schema: StructuredSchema<T>,
  value: unknown,
): Effect.Effect<
  StructuredValidationSuccess<T> | StructuredValidationFailure,
  StructuredSchemaError
> {
  return validateStructuredDataInternalEffect(schema, value).pipe(
    Effect.map((parsed) =>
      parsed.success ? parsed : { success: false as const, issues: parsed.issues },
    ),
  );
}

export function validateStructuredData<T>(
  schema: StructuredSchema<T>,
  value: unknown,
): StructuredValidationSuccess<T> | StructuredValidationFailure {
  return Effect.runSync(
    Effect.match(validateStructuredDataEffect(schema, value), {
      onFailure: (error) => ({ success: false as const, issues: [...error.issues] }),
      onSuccess: (parsed) => parsed,
    }),
  );
}

function validateStructuredDataInternalEffect<T>(
  schema: StructuredSchema<T>,
  value: unknown,
): Effect.Effect<InternalStructuredValidationResult<T>, StructuredSchemaError> {
  return Effect.gen(function* () {
    const normalized = yield* normalizeStructuredSchemaEffect(schema);
    return yield* Effect.try({
      try: () => validateStructuredDataUnsafe(normalized, value),
      catch: () =>
        structuredSchemaFailure('validate', 'Structured data validation could not be completed.'),
    });
  });
}

function validateStructuredDataUnsafe<T>(
  normalized: NormalizedStructuredSchema<T>,
  value: unknown,
): InternalStructuredValidationResult<T> {
  if (normalized.parser) {
    const parsed = normalized.parser.safeParse(value);
    if (parsed.success) {
      return { success: true, data: parsed.data };
    }
    return {
      success: false,
      issues: parsed.error.issues.map((issue) => {
        const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${issue.message}`;
      }),
      safeIssues: parsed.error.issues.map((issue) => {
        const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}invalid value (${issue.code})`;
      }),
    };
  }

  const issues: string[] = [];
  validateJsonSchemaValue(normalized.jsonSchema, value, '$', issues);
  if (issues.length > 0) {
    return { success: false, issues, safeIssues: [...issues] };
  }
  return { success: true, data: value as T };
}

function validateJsonSchemaValue(
  schema: JsonSchema | undefined,
  value: unknown,
  path: string,
  issues: string[],
): void {
  if (!schema || typeof schema !== 'object') return;

  const type = typeof schema.type === 'string' ? schema.type : undefined;
  if (type) {
    validateJsonSchemaType(type, value, path, issues);
    if (issues.length > 0) return;
  }

  if (type === 'object' || (!type && isRecord(value))) {
    validateJsonSchemaObject(schema, value, path, issues);
    return;
  }

  if (type === 'array' || (!type && Array.isArray(value))) {
    validateJsonSchemaArray(schema, value, path, issues);
  }
}

function validateJsonSchemaType(
  type: string,
  value: unknown,
  path: string,
  issues: string[],
): void {
  const valid =
    (type === 'string' && typeof value === 'string') ||
    (type === 'number' && typeof value === 'number' && Number.isFinite(value)) ||
    (type === 'integer' && typeof value === 'number' && Number.isInteger(value)) ||
    (type === 'boolean' && typeof value === 'boolean') ||
    (type === 'null' && value === null) ||
    (type === 'object' && isRecord(value)) ||
    (type === 'array' && Array.isArray(value));

  if (!valid) {
    const actual = value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value;
    issues.push(`${path}: expected ${type}, received ${actual}`);
  }
}

function validateJsonSchemaObject(
  schema: JsonSchema,
  value: unknown,
  path: string,
  issues: string[],
): void {
  if (!isRecord(value)) {
    issues.push(`${path}: expected object`);
    return;
  }

  const properties = isRecord(schema.properties) ? schema.properties : {};
  const required = Array.isArray(schema.required) ? schema.required : [];

  for (const key of required) {
    if (typeof key === 'string' && !(key in value)) {
      issues.push(`${path}.${key}: is required`);
    }
  }

  for (const [key, propertySchema] of Object.entries(properties)) {
    if (!(key in value)) continue;
    validateJsonSchemaValue(propertySchema as JsonSchema, value[key], `${path}.${key}`, issues);
  }
}

function validateJsonSchemaArray(
  schema: JsonSchema,
  value: unknown,
  path: string,
  issues: string[],
): void {
  if (!Array.isArray(value)) {
    issues.push(`${path}: expected array`);
    return;
  }

  const minItems = typeof schema.minItems === 'number' ? schema.minItems : undefined;
  const maxItems = typeof schema.maxItems === 'number' ? schema.maxItems : undefined;
  if (typeof minItems === 'number' && value.length < minItems) {
    issues.push(`${path}: expected at least ${minItems} items`);
  }
  if (typeof maxItems === 'number' && value.length > maxItems) {
    issues.push(`${path}: expected at most ${maxItems} items`);
  }

  const itemSchema = isRecord(schema.items) ? (schema.items as JsonSchema) : undefined;
  if (!itemSchema) return;
  value.forEach((item, index) => {
    validateJsonSchemaValue(itemSchema, item, `${path}[${index}]`, issues);
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
